import { useState, useEffect } from "react";
import PageHead from "@/components/PageHead";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useSpring,
  useReducedMotion,
  type MotionValue,
} from "framer-motion";

const TAGLINES = [
  "Three words. Infinite possibilities. Zero guarantees.",
  "Lesson learned. Never again. Again.",
  "One prompt. Perfect output. Hallucinated everything.",
  "Will replace humans. Any day now. Wrong, but with confidence.",
  "Eat, Sleep, Work, Workout, Repeat",
  "Everything happens for a reason. Stay positive. Reason unclear.",
];

// Floating emojis — fixed layout so they don't jump on re-render
const FLOATERS = [
  { emoji: "💻", x: "8%",  y: "20%", delay: 0,    duration: 5.2, depth: 1.0 },
  { emoji: "⚔️", x: "25%", y: "12%", delay: 2.0,  duration: 5.0, depth: 1.0 },
  { emoji: "🔥", x: "40%", y: "8%",  delay: 1.1,  duration: 6.5, depth: 0.7 },
  { emoji: "🐛", x: "70%", y: "12%", delay: 1.9,  duration: 4.8, depth: 1.0 },
  { emoji: "🤖", x: "88%", y: "22%", delay: 0.8,  duration: 6.1, depth: 0.6 },
  { emoji: "☕", x: "5%",  y: "50%", delay: 0.6,  duration: 6.8, depth: 0.4 },
  { emoji: "🍜", x: "82%", y: "42%", delay: 1.6,  duration: 5.9, depth: 0.7 },
  { emoji: "⚓", x: "18%", y: "68%", delay: 1.4,  duration: 5.7, depth: 0.8 },
  { emoji: "🏴‍☠️", x: "78%", y: "65%", delay: 0.3,  duration: 7.0, depth: 0.5 },
  { emoji: "🌶️", x: "15%", y: "85%", delay: 0.2,  duration: 7.2, depth: 0.6 },
  { emoji: "🏋️", x: "48%", y: "82%", delay: 0.5,  duration: 6.3, depth: 0.8 },
  { emoji: "🌊", x: "72%", y: "88%", delay: 1.7,  duration: 5.5, depth: 0.9 },
];

// Background parallax orbs — slow-drifting blurred blobs at varying Z-depths
const ORBS = [
  { x: "18%", y: "32%", size: 340, hue: "210", depth: 0.18 }, // blue
  { x: "78%", y: "58%", size: 400, hue: "270", depth: 0.12 }, // purple
  { x: "55%", y: "12%", size: 280, hue: "188", depth: 0.22 }, // teal
  { x: "28%", y: "78%", size: 310, hue: "240", depth: 0.15 }, // indigo
];

// 3D orbit rings — tilted planes rotating around the card center
const RINGS = [
  { rotateX: 72, initialZ: 0,   dur: 24, size: 260, opacity: 0.10 },
  { rotateX: 72, initialZ: 45,  dur: 38, size: 350, opacity: 0.07 },
  { rotateX: 58, initialZ: -20, dur: 30, size: 190, opacity: 0.09 },
];

// Floater: floating emoji with independent mouse-parallax per depth layer
type FloaterData = (typeof FLOATERS)[0];

function Floater({
  f,
  mouseX,
  mouseY,
  reduced,
}: {
  f: FloaterData;
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
  reduced: boolean;
}) {
  const rawDx = useTransform(mouseX, [-0.5, 0.5], [-28 * f.depth, 28 * f.depth]);
  const rawDy = useTransform(mouseY, [-0.5, 0.5], [-20 * f.depth, 20 * f.depth]);
  const x = useSpring(rawDx, { stiffness: 50, damping: 22 });
  const y = useSpring(rawDy, { stiffness: 50, damping: 22 });

  return (
    <motion.div
      className="absolute pointer-events-none select-none"
      style={{
        left: f.x,
        top: f.y,
        x,
        y,
        fontSize: `${1.4 + f.depth * 1.2}rem`,
        opacity: 0.35 + f.depth * 0.25,
      }}
    >
      {/* inner handles the floating bob — keeps x/y parallax and y-float composable */}
      <motion.span
        style={{ display: "block" }}
        animate={reduced ? {} : { y: [0, -18 * f.depth, 0], rotate: [0, 8, -8, 0] }}
        transition={{ delay: f.delay, duration: f.duration, repeat: Infinity, ease: "easeInOut" }}
      >
        {f.emoji}
      </motion.span>
    </motion.div>
  );
}

// Orb: blurred gradient blob drifting with mouse at its own parallax depth
type OrbData = (typeof ORBS)[0];

function Orb({
  orb,
  mouseX,
  mouseY,
  reduced,
}: {
  orb: OrbData;
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
  reduced: boolean;
}) {
  const rawDx = useTransform(mouseX, [-0.5, 0.5], [-40 * orb.depth, 40 * orb.depth]);
  const rawDy = useTransform(mouseY, [-0.5, 0.5], [-30 * orb.depth, 30 * orb.depth]);
  const x = useSpring(rawDx, { stiffness: 28, damping: 24 });
  const y = useSpring(rawDy, { stiffness: 28, damping: 24 });

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: orb.x,
        top: orb.y,
        width: orb.size,
        height: orb.size,
        x,
        y,
        translateX: "-50%",
        translateY: "-50%",
        background: `radial-gradient(circle at center, hsla(${orb.hue},70%,70%,0.18) 0%, transparent 70%)`,
        filter: "blur(45px)",
      }}
      animate={reduced ? {} : { scale: [1, 1.08, 1] }}
      transition={{
        duration: 6 + orb.depth * 14,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

export default function HomePage() {
  const [taglineIndex, setTaglineIndex] = useState(
    () => Math.floor(Math.random() * TAGLINES.length)
  );

  useEffect(() => {
    const id = setInterval(() => {
      setTaglineIndex((i) => (i + 1) % TAGLINES.length);
    }, 10_000);
    return () => clearInterval(id);
  }, []);

  const reduced = useReducedMotion() ?? false;

  // Mouse-tracking 3D tilt
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 20 });

  // Specular highlight — a bright dot that chases the mouse across the card surface
  const specX = useSpring(useTransform(mouseX, [-0.5, 0.5], ["5%", "95%"]), { stiffness: 100, damping: 20 });
  const specY = useSpring(useTransform(mouseY, [-0.5, 0.5], ["5%", "95%"]), { stiffness: 100, damping: 20 });

  // Holographic shimmer — iridescent band sweeping across the title with mouse
  const shimmerX = useSpring(
    useTransform(mouseX, [-0.5, 0.5], ["-130%", "130%"]),
    { stiffness: 75, damping: 25 }
  );

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }

  function handleMouseLeave() {
    mouseX.set(0);
    mouseY.set(0);
  }

  return (
    <div
      className="relative flex items-center justify-center min-h-[calc(100vh-12rem)] overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <PageHead description="Personal site — bots, blog, bets." canonicalPath="/" />
      {/* Layer 0 — background parallax orbs */}
      {ORBS.map((orb, i) => (
        <Orb key={i} orb={orb} mouseX={mouseX} mouseY={mouseY} reduced={reduced} />
      ))}

      {/* Layer 1 — 3D orbit rings centered on viewport, behind card */}
      {!reduced && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ perspective: "600px" }}
        >
          {RINGS.map((ring, i) => (
            <motion.div
              key={i}
              className="absolute border border-gray-400 rounded-full"
              style={{
                width: ring.size,
                height: ring.size,
                rotateX: ring.rotateX,
                opacity: ring.opacity,
              }}
              initial={{ rotateZ: ring.initialZ }}
              animate={{ rotateZ: ring.initialZ + 360 }}
              transition={{ duration: ring.dur, repeat: Infinity, ease: "linear" }}
            />
          ))}
        </div>
      )}

      {/* Luffy Gear 5 — the dream */}
      <motion.div
        className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-none select-none"
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 0.7, y: 0 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 180, damping: 20 }}
      >
        <img src="https://media.giphy.com/media/U2fpTR4zq6LuEDFmS1/giphy.gif" alt="Luffy Gear 5" fetchPriority="high" className="w-20 sm:w-32 rounded-xl" />
      </motion.div>

      {/* Zenitsu running — same energy as a dev being replaced by AI */}
      <motion.div
        className="absolute bottom-4 right-4 pointer-events-none select-none"
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 0.7, x: 0 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 180, damping: 20 }}
      >
        <img src="https://media.giphy.com/media/MDddTpKFicmPPRJ819/giphy.gif" alt="Zenitsu running" loading="lazy" className="w-20 sm:w-32 rounded-xl" />
      </motion.div>

      {/* Akaza — the villain arc */}
      <motion.div
        className="absolute bottom-4 left-4 pointer-events-none select-none"
        initial={{ opacity: 0, x: -60 }}
        animate={{ opacity: 0.7, x: 0 }}
        transition={{ delay: 1.5, type: "spring", stiffness: 180, damping: 20 }}
      >
        <img src="https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExODJwbTF1YTY2Zjl1NGJnaGxvaDNrNmNuanVnZnltcHRxZWRtMnZzaiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/zeDVACztUc43ltMmmP/giphy.gif" alt="Akaza" loading="lazy" className="w-20 sm:w-32 rounded-xl" />
      </motion.div>

      {/* Layer 2 — floating emojis, now with per-depth mouse parallax */}
      {FLOATERS.map((f, i) => (
        <Floater key={i} f={f} mouseX={mouseX} mouseY={mouseY} reduced={reduced} />
      ))}

      {/* Layer 3 — 3D tilt card */}
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 800 }}
        className="text-center relative z-10"
      >
        {/* Glow behind title — pushes back in Z */}
        <motion.div
          className="absolute inset-0 rounded-3xl blur-2xl bg-gray-100 -z-10"
          style={{ transform: "translateZ(-30px) scale(1.2)", opacity: 0.6 }}
        />

        {/* Specular highlight — bright dot chasing the mouse across the card */}
        {!reduced && (
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden"
            style={{ transform: "translateZ(-15px)" }}
          >
            <motion.div
              className="absolute w-28 h-28 rounded-full"
              style={{
                left: specX,
                top: specY,
                translateX: "-50%",
                translateY: "-50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.40) 0%, transparent 70%)",
                filter: "blur(10px)",
              }}
            />
          </motion.div>
        )}

        <motion.h2
          className="text-3xl sm:text-4xl font-bold text-gray-900 relative overflow-hidden"
          style={{ transform: "translateZ(30px)" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {"bish-bash-bosh".split("").map((char, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, rotateY: -90, y: -8 }}
              animate={{ opacity: 1, rotateY: 0, y: 0 }}
              transition={{
                delay: i * 0.045,
                type: "spring",
                stiffness: 320,
                damping: 16,
              }}
              style={{
                display: "inline-block",
                whiteSpace: char === "-" ? "normal" : "pre",
                transformStyle: "preserve-3d",
              }}
            >
              {char}
            </motion.span>
          ))}

          {/* Holographic shimmer — iridescent band sweeps across with mouse movement */}
          {!reduced && (
            <motion.span
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                x: shimmerX,
                background:
                  "linear-gradient(105deg, transparent 28%, rgba(139,92,246,0.22) 44%, rgba(99,102,241,0.30) 50%, rgba(59,130,246,0.22) 56%, transparent 72%)",
                mixBlendMode: "overlay",
              }}
            />
          )}
        </motion.h2>

        <div className="mt-3 h-12 sm:h-7 relative" style={{ transform: "translateZ(16px)" }}>
          <AnimatePresence mode="wait">
            <motion.p
              key={taglineIndex}
              className="text-base sm:text-lg text-gray-600 absolute sm:whitespace-nowrap text-center w-[90vw] sm:w-auto"
              style={{ left: "50%", x: "-50%" }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
            >
              {TAGLINES[taglineIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
